import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import { DescriptionCol, DescriptionRow } from '@/components/Description';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import {
  ALPHANUMERIC_MAX_CHARS,
  TEXT_MAX_CHARS,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import { useMinAndMaxValidation } from '@/features/transaction_items/components/Form/TextAndAlphanumeric/hooks/useValidationMinAndMax';
import { useTranslation } from '@/i18n';
import {
  Caption,
  InputNumber,
  Label,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useMemo } from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import styles from './MinAndMaxLengthInputs.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  mode: TransactionItemDrawerMode;
};

const checkIsRangeError = (type?: FieldError['type']) =>
  type === 'min' || type === 'max';

export const MinAndMaxLengthInputs: FC<Props> = memo(({ mode }) => {
  const {
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors },
    clearErrors,
  } = useFormContext<TransactionItemFormType>();

  const componentType = watch('component_type');

  const maxChars = useMemo(() => {
    return componentType === 'text' ? TEXT_MAX_CHARS : ALPHANUMERIC_MAX_CHARS;
  }, [componentType]);

  const { validate } = useMinAndMaxValidation(maxChars);

  const { t } = useTranslation();

  const validateMinAndMax = useCallback(async () => {
    const { min_chars, max_chars } = getValues();
    const validationResult = await validate(min_chars, max_chars);
    Object.entries(validationResult).forEach(([key, value]) => {
      if (value) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        setError(key as keyof TransactionItemFormType, {
          type: value.type,
          message: value.message,
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        clearErrors(key as keyof TransactionItemFormType);
      }
    });
  }, [getValues, setError, validate, clearErrors]);

  const onChange = useCallback(
    (type: keyof TransactionItemFormType, value: string | number | null) => {
      if (type === 'min_chars') {
        setValue(
          'min_chars',
          value === '' || value === null ? undefined : Number(value),
          { shouldDirty: true }
        );
      } else if (type === 'max_chars') {
        setValue(
          'max_chars',
          value === '' || value === null ? undefined : Number(value),
          { shouldDirty: true }
        );
      }
      validateMinAndMax();
    },
    [setValue, validateMinAndMax]
  );

  const nonRangeErrorMessages = useMemo(() => {
    const newErrors = new Set<string>();

    if (errors?.min_chars && !checkIsRangeError(errors?.min_chars.type)) {
      newErrors.add(errors.min_chars?.message || '');
    }
    if (errors?.max_chars && !checkIsRangeError(errors?.max_chars.type)) {
      newErrors.add(errors.max_chars?.message || '');
    }
    return newErrors;
  }, [errors?.min_chars, errors?.max_chars]);

  const isShowRangeErrorMessage = useMemo(
    () =>
      checkIsRangeError(errors?.max_chars?.type) ||
      checkIsRangeError(errors?.min_chars?.type),
    [errors?.min_chars?.type, errors?.max_chars?.type]
  );

  return (
    <DescriptionRow>
      <DescriptionCol
        label={
          <Label
            testId='min_max_label'
            required={mode !== TransactionItemDrawerModeEnum.DETAIL}
          >
            <BoldLabelText>{t('transaction_items.form.min_max')}</BoldLabelText>
          </Label>
        }
        labelWidth='176px'
      />
      <DescriptionCol
        value={
          <div className={cx(styles['container'])}>
            {mode === TransactionItemDrawerModeEnum.DETAIL ? (
              <div>
                {getValues('min_chars')}
                {t('transaction_items.form.over')}
                {getValues('max_chars')}
                {t('transaction_items.under')}
              </div>
            ) : (
              <>
                <div className={cx(styles['input_container'])}>
                  <InputNumber
                    name='min_chars'
                    onChange={(value) => onChange('min_chars', value)}
                    status={errors?.min_chars ? 'error' : undefined}
                    defaultValue={getValues('min_chars')}
                    testId='min_chars'
                  />
                  <div className={cx(styles['min_max_text_container'])}>
                    {t('transaction_items.form.over')}
                  </div>
                  <InputNumber
                    status={errors?.max_chars ? 'error' : undefined}
                    name='max_chars'
                    onChange={(value) => onChange('max_chars', value)}
                    defaultValue={getValues('max_chars') ?? maxChars}
                    testId='max_chars'
                  />
                  <div className={cx(styles['min_max_text_container'])}>
                    {t('transaction_items.under')}
                  </div>
                </div>
                {nonRangeErrorMessages.size > 0 ? (
                  <div>
                    {Array.from(nonRangeErrorMessages).map((error, index) => (
                      <Caption type='error' testId='error-msg' key={index}>
                        {error}
                      </Caption>
                    ))}
                  </div>
                ) : null}
                <Caption
                  type={isShowRangeErrorMessage ? 'error' : 'helper'}
                  testId='length-caption'
                >
                  {t('transaction_items.form.min_and_max.caption', {
                    maxChars,
                  })}
                </Caption>
              </>
            )}
          </div>
        }
      />
    </DescriptionRow>
  );
});
MinAndMaxLengthInputs.displayName = 'MinAndMaxLengthInputs';
